import { useEffect, useState } from 'react';
import { REGIONS } from '../shared/regions';
import { createJobPost, updateJobPost } from '../../services/jobs';
import { supabase } from '../../lib/supabaseClient';

export default function JobPostingForm({ onPosted, editing = null, onCancelEdit }) {
  // 기존 필드
  const [title, setTitle] = useState('');
  const [region, setRegion] = useState('');
  const [district, setDistrict] = useState('');
  const [location, setLocation] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('open');

  // 신규 필드
  const [salary, setSalary] = useState('');
  const [durationType, setDurationType] = useState('하루'); // '1~3시간', '반나절 (4~6시간)', '하루', '하루 이상 일주일', '일주일이상'
  const [workScope, setWorkScope] = useState([]); // ['조제', '매약', '영양제 상담']
  const [dispensingDept, setDispensingDept] = useState(''); // 과명
  const [hiraRegistration, setHiraRegistration] = useState(false); // 심평원 등록 필요 여부
  const [pharmacistCount, setPharmacistCount] = useState(0);
  const [staffCount, setStaffCount] = useState(0);
  const [software, setSoftware] = useState(''); // 전산S/W
  const [atc, setAtc] = useState(''); // 자동조제기
  const [automationDevice, setAutomationDevice] = useState(''); // 자동화기기
  const [keyDuties, setKeyDuties] = useState(''); // 주요업무(한줄)

  const [loading, setLoading] = useState(false);

  const availableDistricts = region ? REGIONS[region] || ['전체 (구)'] : REGIONS[''];

  const durationOptions = ['1~3시간', '반나절 (4~6시간)', '하루', '하루 이상 일주일', '일주일이상'];
  const workScopeOptions = ['조제', '매약', '영양제 상담'];

  useEffect(() => {
    if (editing) {
      setTitle(editing.title || '');
      setRegion(editing.region || '');
      setDistrict(editing.district || '');
      setLocation(editing.location || '');
      setDate(editing.date || '');
      setDescription(editing.description || '');
      setStatus(editing.status || 'open');

      setSalary(editing.salary || '');
      setDurationType(editing.duration_type || '하루');
      setWorkScope(editing.work_scope || []);
      setDispensingDept(editing.dispensing_dept || '');
      setHiraRegistration(editing.hira_registration || false);
      setPharmacistCount(editing.pharmacist_count || 0);
      setStaffCount(editing.staff_count || 0);
      setSoftware(editing.software || '');
      setAtc(editing.atc || '');
      setAutomationDevice(editing.automation_device || '');
      setKeyDuties(editing.key_duties || '');

      if (editing.time) {
        const parts = String(editing.time).split('-').map(s => s.trim());
        if (parts.length === 2) {
          setStartTime(parts[0] || '');
          setEndTime(parts[1] || '');
        } else {
          setStartTime(editing.time || '');
          setEndTime('');
        }
      } else {
        setStartTime('');
        setEndTime('');
      }
    } else {
      resetForm();
    }
  }, [editing]);

  const resetForm = () => {
    setTitle('');
    setRegion('');
    setDistrict('');
    setLocation('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setDescription('');
    setStatus('open');
    setSalary('');
    setDurationType('하루');
    setWorkScope([]);
    setDispensingDept('');
    setHiraRegistration(false);
    setPharmacistCount(0);
    setStaffCount(0);
    setSoftware('');
    setAtc('');
    setAutomationDevice('');
    setKeyDuties('');
  };

  const handleWorkScopeChange = (item) => {
    if (workScope.includes(item)) {
      setWorkScope(workScope.filter(i => i !== item));
    } else {
      setWorkScope([...workScope, item]);
    }
  };

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('로그인이 필요합니다.');

      if (!startTime || !endTime) {
        throw new Error('시작 시간과 종료 시간을 모두 선택해 주세요.');
      }

      const composedTime = `${startTime}-${endTime}`;

      const payload = {
        title,
        region,
        district,
        location,
        date,
        time: composedTime,
        description,
        status,
        salary,
        duration_type: durationType,
        work_scope: workScope,
        dispensing_dept: dispensingDept,
        hira_registration: hiraRegistration,
        pharmacist_count: pharmacistCount,
        staff_count: staffCount,
        software,
        atc,
        automation_device: automationDevice,
        key_duties: keyDuties,
      };

      if (editing) {
        await updateJobPost(editing.id, payload);
      } else {
        await createJobPost(payload);
      }

      onPosted && onPosted();
      if (editing && onCancelEdit) onCancelEdit();
      if (!editing) resetForm();

      alert(editing ? '공고가 수정되었습니다.' : '공고가 등록되었습니다.');
    } catch (err) {
      alert((err && err.message) || '처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-4 sm:p-6 bg-white rounded-2xl shadow-sm border border-slate-200 max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-slate-800">
          {editing ? '공고 수정' : '새 공고 등록'}
        </h3>
        {editing && (
          <button
            type="button"
            onClick={onCancelEdit}
            className="text-slate-400 hover:text-slate-600 text-sm"
          >
            ✕ 취소
          </button>
        )}
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* 기본 정보 그룹 */}
        <div className="space-y-4">
          <div className="group">
            <label className="block text-xs font-semibold text-slate-500 mb-1 ml-1">공고 제목</label>
            <input
              className="w-full border border-slate-200 p-3 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              placeholder="예: 평일 오후 파트타임 약사 구합니다"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="group">
              <label className="block text-xs font-semibold text-slate-500 mb-1 ml-1">약국 이름</label>
              <input
                className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none bg-slate-50"
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="자동 입력 또는 직접 입력"
                required
              />
            </div>
            <div className="group">
              <label className="block text-xs font-semibold text-slate-500 mb-1 ml-1">급여</label>
              <input
                className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={salary}
                onChange={e => setSalary(e.target.value)}
                placeholder="예: 시급 3.5만원 / 협의"
                required
              />
            </div>
          </div>

          <div className="group">
            <label className="block text-xs font-semibold text-slate-500 mb-1 ml-1">약국 주소</label>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <select
                className="border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={region}
                onChange={e => { setRegion(e.target.value); setDistrict(''); }}
                required
              >
                {Object.keys(REGIONS).map(r => (
                  <option key={r} value={r}>{r || '시/도 선택'}</option>
                ))}
              </select>
              <select
                className="border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition disabled:bg-slate-50"
                value={district}
                onChange={e => setDistrict(e.target.value)}
                disabled={!region}
              >
                {availableDistricts.map(d => (
                  <option key={d} value={d === '전체 (구)' ? '' : d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 근무 시간 그룹 */}
        <div className="bg-slate-50 p-4 rounded-2xl space-y-4">
          <label className="block text-sm font-bold text-slate-700">근무 일정</label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1 ml-1">날짜</span>
              <input
                className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:col-span-2 sm:grid-cols-2">
              <div>
                <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1 ml-1">시작</span>
                <input
                  className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  required
                />
              </div>
              <div>
                <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1 ml-1">종료</span>
                <input
                  className="w-full border border-slate-200 p-2.5 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <span className="block text-[10px] uppercase font-bold text-slate-400 mb-2 ml-1">근무 기간</span>
            <div className="flex flex-wrap gap-2">
              {durationOptions.map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setDurationType(opt)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${durationType === opt ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300'}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 업무 범위 */}
        <div className="space-y-3">
          <label className="block text-sm font-bold text-slate-700">업무 범위</label>
          <div className="flex flex-wrap gap-4">
            {workScopeOptions.map(opt => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded text-blue-600 border-slate-300 focus:ring-blue-500 cursor-pointer"
                  checked={workScope.includes(opt)}
                  onChange={() => handleWorkScopeChange(opt)}
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-900 transition">{opt}</span>
                {opt === '조제' && workScope.includes('조제') && (
                  <input
                    className="ml-1 border-b border-slate-200 text-xs py-0.5 outline-none focus:border-blue-500 w-20"
                    placeholder="과명 입력"
                    value={dispensingDept}
                    onChange={e => setDispensingDept(e.target.value)}
                  />
                )}
              </label>
            ))}
          </div>
        </div>

        {/* 근무 환경 및 장비 */}
        <div className="grid grid-cols-1 gap-6 pt-4 border-t border-slate-100">
          <div className="space-y-4">
            <div className="group">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 ml-1">심평원 등록</label>
              <div className="flex gap-2">
                {['필요', '불필요'].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setHiraRegistration(val === '필요')}
                    className={`flex-1 py-2 rounded-xl text-xs font-medium transition ${(hiraRegistration ? '필요' : '불필요') === val ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  >
                    {val}
                  </button>
                ))}
              </div>
            </div>

            <div className="group">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 ml-1">근무 인원</label>
              <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl">
                <div className="flex items-center gap-1.5 flex-1 justify-center">
                  <span className="text-[11px] text-slate-400">약사</span>
                  <input type="number" className="w-12 bg-transparent text-sm font-bold outline-none text-center" value={pharmacistCount} onChange={e => setPharmacistCount(parseInt(e.target.value) || 0)} />
                  <span className="text-xs text-slate-500">명</span>
                </div>
                <div className="w-px h-4 bg-slate-200" />
                <div className="flex items-center gap-1.5 flex-1 justify-center">
                  <span className="text-[11px] text-slate-400">직원</span>
                  <input type="number" className="w-12 bg-transparent text-sm font-bold outline-none text-center" value={staffCount} onChange={e => setStaffCount(parseInt(e.target.value) || 0)} />
                  <span className="text-xs text-slate-500">명</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="group">
                <span className="block text-[10px] font-bold text-slate-400 mb-1 ml-1 uppercase">전산 S/W</span>
                <input className="w-full border border-slate-200 p-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition" value={software} onChange={e => setSoftware(e.target.value)} placeholder="예: 유팜" />
              </div>
              <div className="group">
                <span className="block text-[10px] font-bold text-slate-400 mb-1 ml-1 uppercase">자동조제기</span>
                <input className="w-full border border-slate-200 p-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition" value={atc} onChange={e => setAtc(e.target.value)} placeholder="예: JV-300" />
              </div>
            </div>
            <div className="group">
              <span className="block text-[10px] font-bold text-slate-400 mb-1 ml-1 uppercase">자동화기기 / 기타장비</span>
              <input className="w-full border border-slate-200 p-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition" value={automationDevice} onChange={e => setAutomationDevice(e.target.value)} placeholder="예: 약포기, 봉투자동기 등" />
            </div>
          </div>
        </div>

        {/* 주요 업무 한줄 */}
        <div className="group">
          <label className="block text-sm font-bold text-slate-700 mb-1.5 ml-1">주요 업무 요약</label>
          <input
            className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition"
            placeholder="한 줄로 요약해 주세요 (예: 복약지도 위주의 쾌적한 근무환경입니다)"
            value={keyDuties}
            onChange={e => setKeyDuties(e.target.value)}
          />
        </div>

        {/* 업무 설명 상세 */}
        <div className="group">
          <label className="block text-sm font-bold text-slate-700 mb-1.5 ml-1">상세 내용</label>
          <textarea
            className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition bg-slate-50 min-h-[100px]"
            rows={4}
            placeholder="추가적인 우대사항이나 상세 일정을 입력해 주세요."
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <select
            className="border border-slate-200 px-4 py-3 rounded-xl text-sm font-semibold outline-none bg-white focus:ring-2 focus:ring-blue-500"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="open">모집중</option>
            <option value="closed">마감</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white rounded-xl py-3 text-sm font-bold hover:bg-blue-700 shadow-md shadow-blue-100 transition disabled:opacity-60 active:scale-[0.98]"
          >
            {loading ? '저장 중…' : (editing ? '공고 수정 완료' : '공고 등록하기')}
          </button>
        </div>
      </form>
    </div>
  );
}
